/**
 * Represents a column-based grid system with properties and methods for configuring grid behavior
 * and layout based on device type, parent settings, and custom configurations.
 */
export class ColumnElement {

    isMobileFactory = () => false;
    grid;
    parent;

    /**
     * Retrieves the grid definition associated with the object.
     *
     * @return {Object} The current grid definition.
     */
    get gridDefinition() {
        return this.grid;
    }

    /**
     * Checks whether the current object has a defined grid definition.
     *
     * @return {boolean} Returns true if the grid definition is not null, otherwise false.
     */
    get hasDefinition() {
        return this.gridDefinition != null;
    }

    /**
     * Checks if the grid is turned off based on the grid definition and column count.
     *
     * @return {boolean} Returns true if the grid definition is false or the number of columns is less than or equal to 1. Otherwise, returns false.
     */
    get useGrid() {
        return (this.gridDefinition ?? false) !== false && this.definition.columns > 1;
    }

    /**
     * Retrieves the grid definition based on the current context (mobile or non-mobile).
     *
     * Depending on whether the application is in mobile mode, it returns the appropriate
     * grid configuration. If no grid definition is available, defaults are provided.
     *
     * @return {Object} The grid definition object containing columns and gap properties.
     */
    get definition() {
        return this.useMobile
            ? this.gridDefinition?.mobile ?? { columns: 1, gap: "1rem"}
            : this.gridDefinition ?? { columns: 4, gap: "1rem" };
    }

    /**
     * Determines if the current device is a mobile device by invoking the `isMobileFactory` function.
     * If `isMobileFactory` is not defined or is not a function, it will default to `false`.
     *
     * @return {boolean} Returns `true` if the `isMobileFactory` function exists and returns a truthy value; otherwise, returns `false`.
     */
    get useMobile() {
        if (this.isMobileFactory == null || !(typeof this.isMobileFactory === 'function')) {
            return false;
        }

        return this.isMobileFactory() ?? false;
    }

    /**
     * Retrieves the columns property from the parent object if it exists.
     * If the parent object is null, returns null.
     *
     * @return {Array|null} The columns of the parent object, or null if no parent is present.
     */
    get parentColumns() {
        if (this.parent == null) {
            return null
        }

        return this.parent.columns;
    }

    /**
     * Retrieves the columns configuration for the current instance.
     *
     * If a definition exists, it will return the columns defined in the configuration.
     * Otherwise, it will fall back to the parent columns setting or return a default value of 4.
     *
     * @return {number|Array} The configured columns or the default/fallback value.
     */
    get columns() {
        if (!this.useGrid) {
            return false;
        }

        if (this.hasDefinition) {
            return this.definition.columns;
        }

        return this.parentColumns ?? 4;
    }

    /**
     * Retrieves the gap value defined in the class or returns a default value if not specified.
     * The default value is "1rem".
     * @return {string} The gap value as a string.
     */
    get gap() {
        return this.definition?.gap ?? "1rem";
    }

    /**
     * Constructs an instance and initializes the mobile factory setting.
     *
     * @param {boolean} isMobile - Indicates if the environment is mobile or not.
     * @return {void}
     */
    constructor(isMobile) {
        this.setIsMobileFactory(isMobile);
    }

    /**
     * Sets the mobile factory function or a fixed mobile state.
     *
     * @param {boolean|function} isMobile - A boolean value indicating a fixed mobile state or a function
     *                                       that dynamically determines the mobile state.
     *                                       If a boolean is provided, the mobile state will be fixed.
     *                                       If a function is provided, it will be used to determine the state dynamically.
     * @return {void} This method does not return a value.
     */
    setIsMobileFactory(isMobile) {
        switch (typeof isMobile) {
            case "boolean":
                this.isMobileFactory = () => isMobile;
                break;

            case "function":
                this.isMobileFactory = isMobile;
                break;

            default:
                this.isMobileFactory = () => false;
        }
    }

    /**
     * Defines and sets the grid parameters for the form
     * @param wrapper The form wrapper to set the grid parameters on
     */
    defineGrid(wrapper) {
        if (this.useGrid) {
            wrapper.style.display = 'grid';
            wrapper.style.gridTemplateColumns = `repeat(${this.columns}, 1fr)`;
            wrapper.style.gap = this.gap;
        }
    }

    /**
     * Calculates and returns the grid column span for a form element
     * based on its configuration and parent settings.
     *
     * @param {Object} formElement - The form element object containing
     *                               layout configuration such as columns and columnSpan.
     * @return {string} The grid column span in the format "span {number}".
     */
    elementGridSpan(formElement) {
        const maxColumns = formElement.parentColumns ?? formElement.columns ?? 1;
        if (maxColumns === false) { return null; }
        let columnSpan = `${maxColumns}`;

        if (Number.isInteger(formElement.columnSpan)) {
            columnSpan = formElement.columnSpan;
        }

        return `span ${columnSpan}`;
    }
}